/**
 * Typed API-обёртки для `/auth/*` endpoint'ов (Stage 5.X.2).
 *
 * Эти функции принимают plain типы и возвращают plain типы — никаких
 * side-эффектов на store. Side-эффекты (запись в Zustand) делает caller
 * (`useAuth` hook).
 *
 * См. `apps/miniapp_api/routers/auth.py` + `docs/tg-miniapp.md` §8.1.
 */

import { http } from '@/lib/http';

import type {
  LoginRequest,
  LoginResponse,
  LogoutRequest,
  RefreshRequest,
  RefreshResponse,
} from './types';

/**
 * POST `/auth/login` — обмен Telegram initData на пару JWT + opaque refresh.
 *
 * @param initData полная query-string из `WebApp.initData` (HMAC-подписанная).
 *
 * @throws {ApiError} `AUTH_INVALID_INIT_DATA` (HMAC mismatch),
 *   `AUTH_STALE_INIT_DATA` (auth_date > 24h), `AUTH_USER_DELETED` (403),
 *   `VALIDATION_ERROR` (400 — короткий init_data).
 */
export async function loginWithInitData(initData: string): Promise<LoginResponse> {
  const body: LoginRequest = { init_data: initData };
  return http<LoginResponse>('/auth/login', {
    method: 'POST',
    body,
    noAuth: true, // login сам выдаёт токен, не имеет существующего
  });
}

/**
 * POST `/auth/refresh` — обмен refresh-токена на новую пару.
 *
 * Refresh-токен **rotated** при каждом обращении (старый инвалидируется,
 * новый возвращается). При повторном использовании старого токена →
 * `AUTH_REFRESH_REUSE_DETECTED` + revoke всей цепочки юзера.
 *
 * NB: эта функция не используется UI-кодом напрямую — рефреш делается
 * автоматически в `lib/http.ts` при 401. Эта функция оставлена для
 * полноты типизации + unit-тестов.
 */
export async function refreshAuth(refreshToken: string): Promise<RefreshResponse> {
  const body: RefreshRequest = { refresh_token: refreshToken };
  return http<RefreshResponse>('/auth/refresh', {
    method: 'POST',
    body,
    noAuth: true,
    noRetry: true,
  });
}

/**
 * POST `/auth/logout` — blacklist текущего access-токена + revoke refresh.
 *
 * Требует валидный `Authorization: Bearer <access>`. Body опционален —
 * если передан `refresh_token`, он атомарно revoked'ится; иначе только
 * access blacklist'ится.
 *
 * 204 No Content при успехе.
 *
 * Idempotent с точки зрения backend'а: повторный logout с blacklist'нутым
 * токеном вернёт 401 `AUTH_TOKEN_BLACKLISTED` (это нормально — UI должен
 * локально считать logout успешным).
 */
export async function logoutAuth(refreshToken: string | null): Promise<void> {
  const body: LogoutRequest = refreshToken !== null ? { refresh_token: refreshToken } : {};
  await http<null>('/auth/logout', {
    method: 'POST',
    body,
    noRetry: true,
  });
}
